import React from "react"
import { createPortal } from "react-dom"

type Props = {
  open: boolean
  title: string
  onClose: () => void
  children: React.ReactNode
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'

let titleSeq = 0

// Large-format overlay modal. Closes on backdrop click, the ×, or Escape.
// Traps focus while open, restores focus to the previously-focused element on
// close, locks body scroll, and is labelled by its title for screen readers.
export const Modal: React.FC<Props> = ({ open, title, onClose, children }) => {
  const dialogRef = React.useRef<HTMLDivElement>(null)
  const titleId = React.useRef(`wb-modal-title-${titleSeq++}`).current

  // Keep onClose in a ref so the focus-management effect below doesn't depend on
  // its identity. Callers commonly pass an inline onClose, and re-running this
  // effect on every parent render would steal focus back to the first control
  // mid-typing.
  const onCloseRef = React.useRef(onClose)
  React.useEffect(() => {
    onCloseRef.current = onClose
  })

  React.useEffect(() => {
    if (!open) return

    const previouslyFocused = document.activeElement as HTMLElement | null
    const dialog = dialogRef.current

    // Focus the first focusable control (else the dialog itself) — but leave a
    // field that asked for autoFocus alone. React applies autoFocus during
    // commit, i.e. before this effect, so stealing focus here would immediately
    // blur that field; Formik reads the blur as "touched" and the form opens
    // showing a validation error the user never triggered.
    if (!dialog?.contains(document.activeElement)) {
      const focusables = dialog ? Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)) : []
      ;(focusables[0] ?? dialog)?.focus()
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation()
        onCloseRef.current()
        return
      }
      if (e.key !== "Tab" || !dialog) return
      const items = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (items.length === 0) {
        e.preventDefault()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener("keydown", onKeyDown, true)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    return () => {
      document.removeEventListener("keydown", onKeyDown, true)
      document.body.style.overflow = prevOverflow
      previouslyFocused?.focus?.()
    }
  }, [open])

  if (!open) return null

  return createPortal(
    <div className="fsp-admin__modal-overlay" onClick={onClose}>
      <div
        ref={dialogRef}
        className="fsp-admin__modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="fsp-admin__modal-head">
          <h2 id={titleId}>{title}</h2>
          <button className="fsp-admin__modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="fsp-admin__modal-body">{children}</div>
      </div>
    </div>,
    document.body
  )
}
